import { useEffect, useState, type ReactNode } from 'react'
import {
  listTransactionsOut,
  listTransactionsIn,
  listStkRequests,
  listBalanceQueries,
  getTransactionOutDetail,
  getTransactionInDetail,
  getStkDetail,
  getBalanceDetail,
  recheckPayout,
  type Paged,
  type TransactionDetail,
} from '../api/monitoring'
import type { TransactionOut, TransactionIn, StkRequest, BalanceQuery } from '../api/monitoring'
import { fmtMoney, fmtDateTime, titleCase } from '../lib/format'
import { StatusChip } from '../components/StatusChip'
import { Pagination } from '../components/Pagination'
import { Icon } from '../components/Icon'
import { useUI } from '../store/ui'
import { Can } from '../rbac/Can'

type Col<T> = { header: string; width: string; render: (row: T) => ReactNode }
type Fetcher<T> = (limit: number, offset: number, q: string, stuckOnly: boolean, attentionOnly?: boolean) => Promise<Paged<T>>
type DetailFetcher = (id: string) => Promise<TransactionDetail>

const LIMIT = 20

// Generic paginated, searchable table over a { id } row. `fetcher`/`detailFetcher` must be stable module refs
// so the effects don't loop; the parent keys the table by tab to reset state on tab change.
function PagedTable<T extends { id: string }>({
  fetcher,
  detailFetcher,
  cols,
  empty,
  searchHint,
  showStuckFilter,
  showAttentionFilter,
}: {
  fetcher: Fetcher<T>
  detailFetcher: DetailFetcher
  cols: Col<T>[]
  empty: string
  searchHint: string
  showStuckFilter?: boolean
  showAttentionFilter?: boolean
}) {
  const [items, setItems] = useState<T[] | null>(null)
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [input, setInput] = useState('')
  const [q, setQ] = useState('')
  const [stuckOnly, setStuckOnly] = useState(false)
  const [attentionOnly, setAttentionOnly] = useState(false)
  const [err, setErr] = useState('')
  const [selected, setSelected] = useState<string | null>(null)

  // debounce the search box → applied query (and jump back to the first page)
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(input.trim())
      setOffset(0)
    }, 300)
    return () => clearTimeout(t)
  }, [input])

  useEffect(() => {
    let live = true
    setItems(null)
    fetcher(LIMIT, offset, q, stuckOnly, attentionOnly)
      .then((p) => {
        if (live) {
          setItems(p.items)
          setTotal(p.total)
        }
      })
      .catch((e) => live && setErr(e instanceof Error ? e.message : 'Could not load'))
    return () => {
      live = false
    }
  }, [fetcher, offset, q, stuckOnly, attentionOnly])

  const template = cols.map((c) => c.width).join(' ')
  return (
    <>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
        <input className="input" style={{ maxWidth: 360 }} value={input} onChange={(e) => setInput(e.target.value)} placeholder={searchHint} aria-label="Search transactions" />
        {showStuckFilter && (
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={stuckOnly} onChange={(e) => { setStuckOnly(e.target.checked); setOffset(0) }} aria-label="Stuck only" />
            Stuck only
          </label>
        )}
        {showAttentionFilter && (
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={attentionOnly} onChange={(e) => { setAttentionOnly(e.target.checked); setOffset(0) }} aria-label="Needs attention" />
            Needs attention
          </label>
        )}
      </div>

      {err && <div className="auth-error mt8">{err}</div>}

      <div className="tbl tbl-scroll">
        <div className="tr head" style={{ gridTemplateColumns: template }}>
          {cols.map((c) => (
            <div key={c.header}>{c.header}</div>
          ))}
        </div>
        {items === null &&
          [0, 1, 2, 3].map((i) => (
            <div className="tr body" style={{ gridTemplateColumns: template }} key={i}>
              {cols.map((c) => (
                <div key={c.header} className="skeleton" style={{ width: '60%' }} />
              ))}
            </div>
          ))}
        {items?.map((row) => (
          <div className="tr body click" style={{ gridTemplateColumns: template, cursor: 'pointer' }} key={row.id} onClick={() => setSelected(row.id)}>
            {cols.map((c) => (
              <div key={c.header} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.render(row)}
              </div>
            ))}
          </div>
        ))}
        {items?.length === 0 && <div className="empty">{q ? `No matches for “${q}”` : empty}</div>}
      </div>
      <Pagination total={total} limit={LIMIT} offset={offset} onChange={setOffset} />

      {selected && <DetailDrawer fetcher={detailFetcher} id={selected} onClose={() => setSelected(null)} />}
    </>
  )
}

// ---- detail drawer (generic: any transaction type) --------------------------

const HIDDEN = new Set(['id', 'payload'])

function labelize(key: string): string {
  return key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
}

function renderValue(key: string, val: unknown): ReactNode {
  if (val === null || val === undefined || val === '') return '—'
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  if (key.endsWith('_at')) return fmtDateTime(String(val))
  if (key === 'status') return <StatusChip status={titleCase(String(val))} />
  return String(val)
}

function DetailDrawer({ fetcher, id, onClose }: { fetcher: DetailFetcher; id: string; onClose: () => void }) {
  const [d, setD] = useState<TransactionDetail | null>(null)
  const [err, setErr] = useState('')

  useEffect(() => {
    let live = true
    fetcher(id)
      .then((x) => live && setD(x))
      .catch((e) => live && setErr(e instanceof Error ? e.message : 'Could not load detail'))
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => {
      live = false
      window.removeEventListener('keydown', onKey)
    }
  }, [fetcher, id, onClose])

  const payload = d?.payload
  const hasPayload = payload !== null && payload !== undefined
  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <div className="drawer" role="dialog" aria-label="Transaction detail">
        <div className="drawer-head">
          <b>Transaction detail</b>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="close" size={16} />
          </button>
        </div>
        <div className="drawer-body">
          {err && <div className="auth-error">{err}</div>}
          {!d && !err && [0, 1, 2, 3, 4].map((i) => <div key={i} className="skeleton" style={{ height: 14, margin: '8px 0' }} />)}
          {d && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', rowGap: 8, columnGap: 12, fontSize: 13 }}>
                {Object.entries(d)
                  .filter(([k]) => !HIDDEN.has(k))
                  .map(([k, v]) => (
                    <div key={k} style={{ display: 'contents' }}>
                      <div className="muted" style={{ fontSize: 12 }}>{labelize(k)}</div>
                      <div style={{ overflowWrap: 'anywhere' }}>{renderValue(k, v)}</div>
                    </div>
                  ))}
              </div>
              <div className="card-title mt16">Payload</div>
              {hasPayload ? (
                <pre
                  className="mono"
                  style={{ fontSize: 11.5, background: 'var(--surface-2, rgba(127,127,127,0.08))', padding: 10, borderRadius: 6, overflowX: 'auto', maxHeight: 320 }}
                >
                  {JSON.stringify(payload, null, 2)}
                </pre>
              ) : (
                <div className="muted" style={{ fontSize: 12 }}>No payload recorded for this transaction.</div>
              )}
            </>
          )}
        </div>
        <div className="drawer-foot">
          <button className="btn ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  )
}

const mono = { fontSize: 11.5, color: 'var(--text-2)' } as const
const muted = { fontSize: 12 } as const

// RecheckButton re-queries Safaricom for a stuck/failed payout (resets its status-query backoff). Shown only for
// non-terminal rows with no query already in flight; requires manage:config.
function RecheckButton({ t }: { t: TransactionOut }) {
  const showToast = useUI((s) => s.showToast)
  const [busy, setBusy] = useState(false)
  const actionable = (t.status === 'submitted' || t.status === 'failed') && !t.reconciling
  if (!actionable) return <span className="muted">—</span>
  async function recheck() {
    setBusy(true)
    try {
      await recheckPayout(t.reference)
      showToast('Re-check requested — querying Safaricom now', 'success')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Re-check failed', 'danger')
    } finally {
      setBusy(false)
    }
  }
  return (
    <Can perm="manage:config">
      <button className="btn ghost" style={{ padding: '2px 8px', fontSize: 12 }} disabled={busy} onClick={(e) => { e.stopPropagation(); void recheck() }}>
        {busy ? '…' : 'Re-check'}
      </button>
    </Can>
  )
}

const outCols: Col<TransactionOut>[] = [
  { header: 'Beneficiary', width: 'minmax(150px,1fr)', render: (t) => t.beneficiary || '—' },
  { header: 'Amount', width: '130px', render: (t) => <span className="num">{fmtMoney(t.amount)}</span> },
  {
    header: 'Status',
    width: '200px',
    render: (t) => (
      <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <StatusChip status={titleCase(t.status)} />
        {t.reconciling ? <StatusChip status="Reconciling" /> : t.stuck ? <StatusChip status="Stuck" /> : null}
      </span>
    ),
  },
  { header: 'Reference', width: '150px', render: (t) => <span className="mono" style={mono}>{t.reference}</span> },
  { header: 'Detail', width: 'minmax(120px,1fr)', render: (t) => <span className="muted">{t.fail_reason || t.response_ref || '—'}</span> },
  { header: 'When', width: '140px', render: (t) => <span className="muted" style={muted}>{fmtDateTime(t.created_at)}</span> },
  { header: '', width: '96px', render: (t) => <RecheckButton t={t} /> },
]

const inCols: Col<TransactionIn>[] = [
  { header: 'Payer', width: 'minmax(150px,1fr)', render: (t) => t.payer_name || '—' },
  { header: 'Amount', width: '130px', render: (t) => <span className="num">{fmtMoney(t.amount)}</span> },
  { header: 'Source', width: '130px', render: (t) => <span className="mono" style={mono}>{t.source_number || '—'}</span> },
  { header: 'Reference', width: '140px', render: (t) => <span className="mono" style={mono}>{t.reference || '—'}</span> },
  { header: 'M-Pesa ref', width: '150px', render: (t) => <span className="mono" style={mono}>{t.transaction_id}</span> },
  { header: 'When', width: '140px', render: (t) => <span className="muted" style={muted}>{fmtDateTime(t.created_at)}</span> },
]

const stkCols: Col<StkRequest>[] = [
  { header: 'Phone', width: '140px', render: (t) => <span className="mono" style={mono}>{t.phone}</span> },
  { header: 'Amount', width: '120px', render: (t) => <span className="num">{fmtMoney(t.amount)}</span> },
  { header: 'Status', width: '110px', render: (t) => <StatusChip status={titleCase(t.status)} /> },
  { header: 'Reference', width: '140px', render: (t) => <span className="mono" style={mono}>{t.reference || '—'}</span> },
  { header: 'Receipt', width: '150px', render: (t) => <span className="mono" style={mono}>{t.mpesa_receipt || '—'}</span> },
  { header: 'When', width: '140px', render: (t) => <span className="muted" style={muted}>{fmtDateTime(t.created_at)}</span> },
]

const balCols: Col<BalanceQuery>[] = [
  { header: 'Account', width: 'minmax(150px,1fr)', render: (t) => t.account_name || t.account_number },
  { header: 'Status', width: '110px', render: (t) => <StatusChip status={titleCase(t.status)} /> },
  { header: 'Currency', width: '90px', render: (t) => t.currency || '—' },
  { header: 'Working', width: '130px', render: (t) => <span className="num">{fmtMoney(t.working_balance, t.currency || 'KES')}</span> },
  { header: 'Available', width: '130px', render: (t) => <span className="num">{fmtMoney(t.available_balance, t.currency || 'KES')}</span> },
  { header: 'When', width: '140px', render: (t) => <span className="muted" style={muted}>{fmtDateTime(t.created_at)}</span> },
]

const TABS = [
  { key: 'out', label: 'Outflows' },
  { key: 'in', label: 'Inflows' },
  { key: 'stk', label: 'STK pushes' },
  { key: 'bal', label: 'Balances' },
] as const
type TabKey = (typeof TABS)[number]['key']

export function Transactions() {
  const [tab, setTab] = useState<TabKey>('out')
  return (
    <div className="page">
      <div className="ph">
        <div>
          <h1>Transactions</h1>
          <div className="desc">Money in and out across the M-Pesa rails — collections, payouts, STK prompts and balance checks.</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, margin: '4px 0 12px' }}>
        {TABS.map((t) => (
          <button key={t.key} className={`btn sm ${tab === t.key ? 'primary' : 'ghost'}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'out' && <PagedTable key="out" fetcher={listTransactionsOut} detailFetcher={getTransactionOutDetail} cols={outCols} empty="No payouts yet" searchHint="Search reference, instruction id, phone, name, receipt…" showStuckFilter showAttentionFilter />}
      {tab === 'in' && <PagedTable key="in" fetcher={listTransactionsIn} detailFetcher={getTransactionInDetail} cols={inCols} empty="No collections yet" searchHint="Search reference, M-Pesa ref, phone, payer…" />}
      {tab === 'stk' && <PagedTable key="stk" fetcher={listStkRequests} detailFetcher={getStkDetail} cols={stkCols} empty="No STK pushes yet" searchHint="Search reference, phone, checkout id, receipt…" />}
      {tab === 'bal' && <PagedTable key="bal" fetcher={listBalanceQueries} detailFetcher={getBalanceDetail} cols={balCols} empty="No balance queries yet" searchHint="Search account name or number…" />}
    </div>
  )
}
